import React from "react";
import { useNavigate } from "react-router-dom";

import { themevals } from "theme/themevals";
import BasicModal from "components/ui/BasicModal";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { Pill } from "containers/Leads/List";
import type { MoGenerateResult, MoProposalLabel } from "api/MaintenanceOpportunities";

/**
 * What "Generate proposals" made (2026-10-19): one row per ticked
 * opportunity with its link and password to paste into an email, or why it
 * was skipped. Nothing here is sent - the person sends it, or Mission
 * Control drafts it into the Outbox.
 */

export const PROPOSAL_PILL: Record<MoProposalLabel, { label: string; bg: string; fg: string }> = {
  draft: { label: "Draft", bg: "#F1EFE8", fg: "#5f5e5a" },
  ready: { label: "Link ready", bg: "#E6F1FB", fg: "#185FA5" },
  sent: { label: "Sent", bg: "#FFF8E1", fg: "#7A5A00" },
  viewed: { label: "Viewed", bg: "#E1F5EE", fg: "#0F6E56" },
  accepted: { label: "Accepted", bg: "#0F6E56", fg: "#fff" },
  lost: { label: "Lost", bg: "#FBEAEA", fg: "#A60000" },
};

const REASON: Record<string, string> = {
  exists: "Already had a link - returned it",
  already_sent: "Already sent - nothing new made",
  draft_in_wizard: "A draft exists - finish it in the wizard",
  no_end_customer: "Skipped: nobody named as the owner yet",
  no_coordinates: "Skipped: site has no map position",
  no_covering_depot: "Skipped: no depot covers this site",
  not_open: "Skipped: opportunity is closed",
  error: "Failed",
};

const cell: React.CSSProperties = { padding: "6px 8px", borderBottom: "1px solid #EEF0F2", fontFamily: "Poppins", fontSize: 12, verticalAlign: "top" };
const linkBtn: React.CSSProperties = { background: "none", border: "none", color: "#185FA5", cursor: "pointer", fontFamily: "Poppins", fontSize: 12, padding: 0 };

export const copyText = async (text: string, toast: any, what = "Copied") => {
  try {
    await navigator.clipboard.writeText(text);
    toast.add(what, "var(--theme-ui-colors-success)");
  } catch {
    toast.add("Could not copy - select and copy it by hand.", "var(--theme-ui-colors-warning)");
  }
};

const GeneratedProposals: React.FC<{
  results: MoGenerateResult[];
  siteNames: Record<string, string>;
  onClose: () => void;
}> = ({ results, siteNames, onClose }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const withLink = results.filter((r) => r.shareUrl);
  const made = results.filter((r) => r.created).length;

  const copyAll = () =>
    copyText(
      ["Site\tOwner\tEmail\tLink\tPassword", ...withLink.map((r) => [siteNames[r.opportunityId] ?? "", r.prospectName ?? "", r.recipient?.email ?? "", r.shareUrl ?? "", r.password ?? ""].join("\t"))].join("\n"),
      toast,
      `Copied ${withLink.length} row${withLink.length === 1 ? "" : "s"} (paste into a sheet)`,
    );

  return (
    <BasicModal
      size="lg"
      title={`${withLink.length} link${withLink.length === 1 ? "" : "s"} ready${made !== withLink.length ? ` (${made} new)` : ""}${results.length > withLink.length ? `, ${results.length - withLink.length} skipped` : ""}`}
      cancel_click={onClose}
      cancel_text="Close"
      actions={withLink.length ? <BasicButton type="button" keepText onClick={copyAll}>Copy all links + passwords</BasicButton> : null}
    >
      <div style={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey, marginBottom: 10, textAlign: "left" }}>
        Nothing has been emailed. Each link is the frozen document; send it with its password, then the row moves to Sent when the send is recorded.
      </div>
      <div style={{ maxHeight: "55vh", overflow: "auto", textAlign: "left" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["Site / owner", "Send to", "Link", "Password", ""].map((h) => (
                <th key={h} style={{ ...cell, fontWeight: 600, color: themevals.colors.dkgrey, position: "sticky", top: 0, background: "#fff" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.map((r) => {
              const pill = r.status ? PROPOSAL_PILL[r.status] : null;
              return (
                <tr key={r.opportunityId}>
                  <td style={cell}>
                    <div style={{ fontWeight: 600 }}>{siteNames[r.opportunityId] ?? r.opportunityId.slice(0, 8)}</div>
                    <div style={{ color: themevals.colors.dkgrey }}>{r.prospectName ?? ""}</div>
                  </td>
                  <td style={cell}>{r.recipient?.email ? <>{r.recipient.name ? `${r.recipient.name} ` : ""}<span style={{ color: themevals.colors.dkgrey }}>{r.recipient.email}</span></> : r.shareUrl ? <span style={{ color: "#7A5A00" }}>No email on file</span> : ""}</td>
                  <td style={cell}>
                    {r.shareUrl ? <button type="button" style={linkBtn} onClick={() => copyText(r.shareUrl as string, toast, "Link copied")}>Copy link</button> : null}
                    {r.proposalId && r.status !== "draft" ? <> · <button type="button" style={linkBtn} onClick={() => navigate(`/proposals/${r.proposalId}`)}>Open</button></> : null}
                    {r.proposalId && r.status === "draft" ? <button type="button" style={linkBtn} onClick={() => navigate(`/proposals/${r.proposalId}/edit`)}>Finish draft</button> : null}
                  </td>
                  <td style={cell}>
                    {r.password ? <button type="button" style={{ ...linkBtn, fontFamily: "monospace", fontSize: 12 }} title="Copy" onClick={() => copyText(r.password as string, toast, "Password copied")}>{r.password}</button> : r.shareUrl ? "None" : ""}
                  </td>
                  <td style={cell}>
                    {r.created ? (pill ? <Pill bg={pill.bg} fg={pill.fg}>{pill.label}</Pill> : null) : (
                      <span style={{ color: r.reason === "error" || r.reason?.startsWith("no_") ? "#A60000" : themevals.colors.dkgrey }}>
                        {REASON[r.reason ?? ""] ?? ""}{r.reason === "error" && r.message ? `: ${r.message}` : ""}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </BasicModal>
  );
};

export default GeneratedProposals;
